/**
 * ESC/POS receipt rendering using bizpluspos-print-core.
 * Mirrors layout logic from Receipt.tsx DynamicTable (on-screen receipt unchanged).
 */
import {
  OmnithermError,
  OmnithermErrorCode,
  print,
  ThermalDocument,
  scanBleDevices,
  getBondedBleDevices,
  discoverBleDevices,
  ensureNativeBleReady,
  scanUsbDevices,
  WebBluetoothTransport,
  isQzAvailable,
  isWebUsbAvailable,
  isWebBluetoothAvailable,
  isWebSerialAvailable,
  listQzPrinters,
  scanSerialPorts,
} from './thermalPrintCore';
import type {
  ConnectionConfig,
  BleDevice,
  UsbDevice,
  SerialPortDevice,
  DiscoveredBleDevice,
  DiscoverBleOptions,
} from './thermalPrintCore';
import { Platform } from 'react-native';
import { isElectronRuntime } from '../utils/isElectron';
import {
  formatNumberDecimal,
  formatWithCommas,
  replaceCurrencyString,
  roundToNumericPrecision,
} from '../utils/libs';
import { formatDisplayDateTime } from '../utils/formatDisplayDateTime';
import { getOrderLineProductDisplayName } from '../utils/productDisplayName';
import type {
  DynamicTableParams,
  OrderProductItem,
  Payment_Information,
} from '../screens/checkout/components/Receipt';
import { buildReceiptTotalsSummary } from './receiptTotals';

export {
  scanBleDevices,
  getBondedBleDevices,
  discoverBleDevices,
  ensureNativeBleReady,
  scanUsbDevices,
  scanSerialPorts,
  isQzAvailable,
  isWebUsbAvailable,
  isWebBluetoothAvailable,
  isWebSerialAvailable,
  listQzPrinters,
};
export type {
  BleDevice,
  UsbDevice,
  SerialPortDevice,
  DiscoveredBleDevice,
  DiscoverBleOptions,
};

export type ThermalPrinterTarget =
  | {
      mode: 'bluetooth';
      macAddress: string;
    }
  | {
      mode: 'tcp';
      host: string;
      port: number;
      timeoutMs?: number;
    }
  | {
      mode: 'usb';
      vendorId: string;
      productId: string;
    }
  | {
      mode: 'network';
      printerName: string;
    }
  | {
      mode: 'web-bluetooth';
      bleServiceUuid?: string;
      bleCharacteristicUuid?: string;
      bleMtu?: number;
      bleDeviceId?: string;
      bleDeviceName?: string;
      bleNamePrefix?: string;
    }
  | {
      mode: 'web-serial';
      serialBaudRate: number;
      serialDataBits?: 7 | 8;
      serialStopBits?: 1 | 2;
      serialParity?: 'none' | 'even' | 'odd';
      serialFlowControl?: 'none' | 'hardware';
      serialBufferSize?: number;
      serialUsbVendorId?: string;
      serialUsbProductId?: string;
    }
  | {
      mode: 'serial';
      path: string;
      baudRate: number;
    }
  | {
      mode: 'spooler';
      printerName: string;
    };

export type WebBluetoothThermalProfile = {
  id: string;
  label: string;
  bleServiceUuid: string;
  bleCharacteristicUuid: string;
};

export const WEB_BLUETOOTH_THERMAL_PROFILES: WebBluetoothThermalProfile[] = [
  {
    id: 'generic-18f0',
    label: 'Generic BLE thermal printer',
    bleServiceUuid: '000018f0-0000-1000-8000-00805f9b34fb',
    bleCharacteristicUuid: '00002af1-0000-1000-8000-00805f9b34fb',
  },
  {
    id: 'e7810a71',
    label: 'BLE UART thermal printer',
    bleServiceUuid: 'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
    bleCharacteristicUuid: 'bef8d6c9-9c21-4c9e-b632-bd58c1009f9f',
  },
  {
    id: 'issc-transparent-uart',
    label: 'ISSC Transparent UART',
    bleServiceUuid: '49535343-fe7d-4ae5-8fa9-9fafd205e455',
    bleCharacteristicUuid: '49535343-8841-43f4-a8d4-ecbe34729bb3',
  },
];

export const DEFAULT_WEB_BLUETOOTH_THERMAL_PROFILE =
  WEB_BLUETOOTH_THERMAL_PROFILES[0];

export async function pairWebBluetoothThermalPrinter(): Promise<{
  id?: string;
  name: string;
  profile: WebBluetoothThermalProfile;
}> {
  const bt = (navigator as any)?.bluetooth;
  if (!bt) throw new Error('Web Bluetooth is not available in this browser.');

  const allServiceUuids = WEB_BLUETOOTH_THERMAL_PROFILES.map(
    p => p.bleServiceUuid,
  );

  // Show ALL nearby BLE devices — no filtering at the picker
  const device: any = await bt.requestDevice({
    acceptAllDevices: true,
    optionalServices: allServiceUuids,
  });

  if (!device?.gatt) {
    throw new Error('Selected device does not support Bluetooth GATT.');
  }

  const server = await device.gatt.connect();

  // Probe each known thermal printer profile to see if the device exposes it
  let matchedProfile: WebBluetoothThermalProfile | null = null;
  for (const profile of WEB_BLUETOOTH_THERMAL_PROFILES) {
    try {
      const svc = await server.getPrimaryService(profile.bleServiceUuid);
      await svc.getCharacteristic(profile.bleCharacteristicUuid);
      matchedProfile = profile;
      break;
    } catch {
      // Service/characteristic not found — try next profile
    }
  }

  server.disconnect();

  if (!matchedProfile) {
    const label = device.name || 'Selected device';
    throw new Error(
      `"${label}" is not a supported thermal printer. No known printer service was found on this device.`,
    );
  }

  return {
    id: device.id,
    name: device.name || 'BLE Printer',
    profile: matchedProfile,
  };
}

function getErrorText(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err || '');
}

function getHostDesktopOs(): 'windows' | 'macos' | 'other' {
  if (typeof navigator === 'undefined') return 'other';
  const uaDataPlatform = (navigator as any)?.userAgentData?.platform;
  const platform = String(
    uaDataPlatform || navigator.platform || '',
  ).toLowerCase();
  const ua = String(navigator.userAgent || '').toLowerCase();
  const source = `${platform} ${ua}`;
  if (source.includes('win')) return 'windows';
  if (source.includes('mac')) return 'macos';
  return 'other';
}

function isDesktopUsbDriverError(err: unknown): boolean {
  const text = getErrorText(err);
  return (
    text.includes('LIBUSB_ERROR_NOT_SUPPORTED') ||
    text.includes('USB connection failed')
  );
}

function getDesktopUsbSetupMessage(): string {
  const hostOs = getHostDesktopOs();
  if (hostOs === 'windows') {
    return 'Add this printer in Windows Settings > Bluetooth & devices > Printers & scanners, then select it under OS Printer.';
  }
  if (hostOs === 'macos') {
    return 'Add this printer in System Settings > Printers & Scanners, then select it under OS Printer.';
  }
  return 'Add this printer in your system printer settings, then select it under OS Printer.';
}

export function getThermalPrintErrorMessage(err: unknown): string {
  const isWeb = Platform.OS === 'web';
  if (isDesktopUsbDriverError(err)) {
    return getDesktopUsbSetupMessage();
  }
  if (getErrorText(err).includes('Bluetooth print timed out')) {
    return 'Bluetooth print timed out. Make sure the printer is on and nearby, then try again.';
  }
  const errorText = getErrorText(err).toLowerCase();
  if (
    errorText.includes('bluetooth adapter is not enabled') ||
    errorText.includes('bluetooth is not enabled')
  ) {
    return 'Bluetooth is off. Turn on Bluetooth, then try printing again.';
  }
  if (errorText.includes('can not find the specified printing device')) {
    return "Printer not reachable. Make sure it is switched on, in range, and paired in your phone's Bluetooth settings.";
  }
  if (errorText.includes('bluetooth permission denied')) {
    return 'Bluetooth permission is required to print. Grant it and try again.';
  }
  if (!(err instanceof OmnithermError)) {
    return 'Printing failed. Check printer connection and settings.';
  }
  switch (err.code) {
    case OmnithermErrorCode.DEVICE_NOT_FOUND:
      return isWeb
        ? 'Printer not found. Make sure it is connected and try again from Printer Settings.'
        : 'Printer not found. Re-scan and select the correct printer.';
    case OmnithermErrorCode.CONNECTION_TIMEOUT:
      return 'Printer connection timed out. Check signal/network and try again.';
    case OmnithermErrorCode.CONNECTION_FAILED:
      return 'Could not connect to printer. Check power and connection details.';
    case OmnithermErrorCode.PERMISSION_DENIED:
      return isWeb
        ? 'Printer access was denied. Please allow access when prompted and try again.'
        : 'Printer permission denied. Grant permission and retry.';
    case OmnithermErrorCode.INVALID_CONFIG:
      return isWeb
        ? 'Printer settings are incomplete. Set up a printer in Printer Settings first.'
        : 'Printer settings are invalid. Review address/port or device IDs.';
    case OmnithermErrorCode.NO_TRANSPORT_AVAILABLE:
      return isWeb
        ? 'No printer connection available. Connect a USB printer or set up network printing in Settings.'
        : 'No compatible print transport is available on this device.';
    case OmnithermErrorCode.WRITE_FAILED:
      return 'Connected but failed to send print data to the printer.';
    default:
      return (
        err.message || 'Printing failed. Check printer connection and settings.'
      );
  }
}

function calculatePartialTotals(order: OrderProductItem[]) {
  return order.reduce((total, Item) => {
    const { bp_product_information } = Item;
    if (bp_product_information.bp_product_price_type === 'unit') {
      const itemTotal =
        bp_product_information.bp_product_selling_price *
        ((Item.bp_order_mass ?? 0) - (Item.bp_order_paid_mass ?? 0));
      return total + itemTotal;
    }
    const currentQuantity =
      Item.bp_order_quantity - (Item.bp_order_paid_quantity ?? 0);
    const itemTotal =
      bp_product_information.bp_product_selling_price * currentQuantity;
    return total + itemTotal;
  }, 0);
}

const getOrderLineDisplayName = (item: OrderProductItem): string =>
  getOrderLineProductDisplayName(item);

function toConnectionConfig(target: ThermalPrinterTarget): ConnectionConfig {
  if (target.mode === 'bluetooth') {
    return { type: 'bluetooth', macAddress: target.macAddress };
  }
  if (target.mode === 'tcp') {
    return {
      type: 'tcp',
      host: target.host,
      port: target.port,
      timeout: target.timeoutMs,
    };
  }
  if (target.mode === 'network') {
    return { type: 'tcp', printerName: target.printerName };
  }
  if (target.mode === 'web-bluetooth') {
    const profile = DEFAULT_WEB_BLUETOOTH_THERMAL_PROFILE;
    return {
      type: 'bluetooth',
      bleServiceUuid: target.bleServiceUuid || profile.bleServiceUuid,
      bleCharacteristicUuid:
        target.bleCharacteristicUuid || profile.bleCharacteristicUuid,
      bleNamePrefix: target.bleNamePrefix,
    };
  }
  if (target.mode === 'web-serial') {
    const serialUsbVendorId = parseOptionalUsbId16(target.serialUsbVendorId);
    const serialUsbProductId = parseOptionalUsbId16(target.serialUsbProductId);
    return {
      type: 'serial',
      serialBaudRate: target.serialBaudRate,
      serialDataBits: target.serialDataBits,
      serialStopBits: target.serialStopBits,
      serialParity: target.serialParity,
      serialFlowControl: target.serialFlowControl,
      serialBufferSize: target.serialBufferSize,
      serialUsbVendorId,
      serialUsbProductId,
    };
  }
  if (target.mode === 'serial') {
    return {
      type: 'serial',
      path: target.path,
      baudRate: target.baudRate,
    };
  }
  if (target.mode === 'spooler') {
    return {
      type: 'spooler',
      printerName: target.printerName,
    };
  }

  const vendorId = parseUsbId16(target.vendorId);
  const productId = parseUsbId16(target.productId);
  if (Number.isNaN(vendorId) || Number.isNaN(productId)) {
    throw new Error('Invalid USB vendor or product ID');
  }
  return { type: 'usb', vendorId, productId };
}

function parseUsbId16(raw: string): number {
  const t = raw.trim();
  if (!t) return NaN;
  const n = /^0x/i.test(t)
    ? parseInt(t.replace(/^0x/i, ''), 16)
    : parseInt(t, 10);
  if (!Number.isFinite(n) || n < 0 || n > 0xffff) return NaN;
  return n;
}

function parseOptionalUsbId16(raw: string | undefined): number | undefined {
  const value = String(raw || '').trim();
  if (!value) return undefined;
  const parsed = parseUsbId16(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function getDocWidth(widthMM?: number): 58 | 80 {
  return (widthMM ?? 58) >= 72 ? 80 : 58;
}

/**
 * Renders the same receipt content as DynamicTable using ThermalDocument.
 */
export async function printOrderThermalReceipt(
  { receipt, business, employeedata, currency, totalsMode }: DynamicTableParams,
  {
    charsPerLine,
    autoCut,
    widthMM,
  }: { charsPerLine: number; autoCut: boolean; widthMM?: number },
  target: ThermalPrinterTarget,
): Promise<void> {
  const chars = Math.max(24, Math.min(48, charsPerLine || 32));
  const doc = new ThermalDocument({
    width: getDocWidth(widthMM),
    charsPerLine: chars,
  });
  const priceW = 10;
  const amtW = 7;
  const itemW = Math.max(8, chars - priceW - amtW);
  const widths: [number, number, number] = [itemW, priceW, amtW];

  const currencyText = replaceCurrencyString('Ksh', currency);
  const date = formatDisplayDateTime(new Date());

  const name = (business?.name || '').trim();
  const desc = (business?.description || '').trim();
  const phone = employeedata?.bp_owner_phone_number || 'N/A';
  const employeeName = employeedata?.bp_employee_name || '';
  const receiptSource = receipt as typeof receipt & {
    receiptID?: string;
    bp_order_master_receipt_reference_id?: string;
  };
  const receiptId = String(
    receiptSource?.ReceiptID ||
      receiptSource?.receiptID ||
      receiptSource?.bp_order_master_receipt_reference_id ||
      '',
  ).trim();

  // Header: manually padded because some printer transports ignore align commands.
  doc.line(centerLine(name, chars), { bold: true, height: 2 });
  const descLines = !desc.length
    ? []
    : desc.match(new RegExp('.{1,' + chars + '}', 'g')) || [desc];
  for (const line of descLines) {
    const seg = line.trim();
    if (!seg) continue;
    doc.line(centerLine(seg, chars), { bold: true });
  }
  doc.line(centerLine(`TEL ${phone}`, chars)).feed(1);

  // Column headers
  doc.line(row(['# Item', 'Price', 'Amount'], widths), { bold: true });

  // Order items
  const order = receipt?.order || [];
  for (let idx = 0; idx < order.length; idx++) {
    const item = order[idx];
    if (typeof item !== 'object' || !item.bp_product_information) continue;

    const unitSuffix =
      item.bp_product_information.bp_product_price_type === 'unit'
        ? item.bp_product_information.bp_product_unit_of_mass || ''
        : '';

    const itemDesc = getOrderLineDisplayName(item);

    const currQuantity = roundToNumericPrecision(
      item.bp_product_information.bp_product_price_type === 'unit'
        ? (item.bp_order_mass ?? 0) - (item.bp_order_paid_mass ?? 0)
        : item.bp_order_quantity - (item.bp_order_paid_quantity ?? 0),
    );

    const price = item.bp_product_information.bp_product_selling_price;
    const priceStr = `${formatNumberDecimal(currQuantity)}${unitSuffix}x${price}`;
    const amtStr = String(
      formatWithCommas(roundToNumericPrecision(price * currQuantity)),
    );
    const itemLabel = `${idx + 1}.${itemDesc}`;
    const labelLines = wrapItemLabelToItemColumnWidth(itemLabel, itemW);
    doc.line(row([labelLines[0], priceStr, amtStr], widths));
    for (let li = 1; li < labelLines.length; li++) {
      doc.line(`  ${labelLines[li]}`);
    }
  }

  // Totals section
  const pi: Payment_Information = receipt.Payment_Information;
  const totalAmount = calculatePartialTotals(order);
  const summary = buildReceiptTotalsSummary(pi, totalAmount, totalsMode ?? 'print');

  doc.feed(1);
  const totalsValueW = Math.min(chars - 14, Math.max(12, amtW + 5));
  for (const r of summary.rows) {
    const valueStr =
      r.emphasis === 'change'
        ? formatWithCommas(r.amount, 0, true)
        : formatWithCommas(r.amount);
    doc.line(
      totalsRow(r.label, `${currencyText}${valueStr}`, chars, totalsValueW),
      { bold: r.strong },
    );
  }
  if (summary.rows.length > 0) doc.feed(1);

  // Total line - centered, larger font
  const totalLineVal = formatWithCommas(summary.grandTotal);
  doc
    .text(`Sub-Total ${currencyText}${totalLineVal}`, {
      align: 'center',
      bold: true,
      height: 2,
    })
    .feed(1);

  if (summary.isPartialLedger && summary.isPaidInFull) {
    doc
      .line(centerLine('*** PAID IN FULL ***', chars), {
        bold: true,
      })
      .feed(1);
  }

  // Separator
  doc.line('.'.repeat(Math.min(chars, 28)), { align: 'center' });

  // Footer - compact, no extra spacing
  doc.line('Customer :');
  if (receiptId) {
    doc.line(`Receipt ID: ${receiptId}`);
  }
  doc.line(`Date:${date}`);
  doc.line(`You were served by ${employeeName}`);
  doc.line('GOODS ONCE SOLD WILL NOT BE RE-ACCEPTED', {
    align: 'center',
  });
  doc.line(`Thank you for choosing ${name}!`, { bold: true });
  doc.feed(1).line(centerLine('BizPlus POS - +254759266327', chars), {
    bold: true,
    height: 2,
  });
  doc.cut('full', { feed: Platform.OS === 'web' ? 3 : 0 });
  // if (autoCut) {
  //   doc.cut('full', { feed: getCutFeedLines() });
  // }

  await executePrint(doc, target);
}

async function executePrint(
  doc: ThermalDocument,
  target: ThermalPrinterTarget,
): Promise<void> {
  if (isElectronRuntime()) {
    if (target.mode === 'web-bluetooth') {
      await setElectronBluetoothPrintTarget(target);
    }
    const { print: electronPrint } = await import('./electronPrintService');
    try {
      const printPromise = electronPrint(doc, toConnectionConfig(target));
      await (target.mode === 'web-bluetooth'
        ? withTimeout(printPromise, 7000, 'Bluetooth print timed out')
        : printPromise);
    } finally {
      if (target.mode === 'web-bluetooth') {
        await clearElectronBluetoothPrintTarget();
      }
    }
    return;
  }
  if (target.mode === 'bluetooth') {
    await ensureNativeBleReady(target.macAddress);
  }
  await print(doc, toConnectionConfig(target));
}

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string,
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(label)), ms),
    ),
  ]);
}

async function setElectronBluetoothPrintTarget(
  target: Extract<ThermalPrinterTarget, { mode: 'web-bluetooth' }>,
): Promise<void> {
  const ipcRenderer = (window as any)?.electron?.ipcRenderer;
  if (!ipcRenderer?.invoke) return;
  await ipcRenderer.invoke('bluetooth:set-print-target', {
    deviceId: target.bleDeviceId,
    deviceName: target.bleDeviceName,
    namePrefix: target.bleNamePrefix || target.bleDeviceName,
  });
}

async function clearElectronBluetoothPrintTarget(): Promise<void> {
  const ipcRenderer = (window as any)?.electron?.ipcRenderer;
  if (!ipcRenderer?.invoke) return;
  await ipcRenderer.invoke('bluetooth:clear-print-target');
}

/**
 * Word-wrap item label to the item column width (same as first arg to `row()`).
 * Continuation lines are printed left-aligned under the item column with a small indent.
 */
function wrapItemLabelToItemColumnWidth(
  text: string,
  maxWidth: number,
): string[] {
  const t = text.trim();
  if (!t) {
    return [''];
  }
  const w = Math.max(1, maxWidth);
  const words = t.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= w) {
      current = candidate;
      continue;
    }
    if (current) {
      lines.push(current);
      current = '';
    }
    if (word.length <= w) {
      current = word;
    } else {
      let rest = word;
      while (rest.length > w) {
        lines.push(rest.slice(0, w));
        rest = rest.slice(w);
      }
      current = rest;
    }
  }
  if (current) {
    lines.push(current);
  }
  return lines.length ? lines : [t.slice(0, w)];
}

/**
 * Build a simple left-padded row for column printing inside a single payload string.
 * `widths` is [itemCol, priceCol, amountCol]; total should equal `chars`.
 */
function row(
  cols: [string, string, string],
  widths: [number, number, number],
): string {
  const [a, b, c] = cols;
  const [wa, wb, wc] = widths;
  const colA = a.slice(0, wa).padEnd(wa, ' ');
  const colB = b.slice(0, wb).padStart(wb, ' ');
  const colC = c.slice(0, wc).padStart(wc, ' ');
  return colA + colB + colC;
}

/**
 * Two-column totals row: label left-aligned, value right-aligned.
 * Wider label area than the 3-column item row so labels like "Paid Earlier" fit.
 */
function totalsRow(
  label: string,
  value: string,
  chars: number,
  valueWidth: number,
): string {
  const labelW = Math.max(1, chars - valueWidth - 1);
  const labelPart = label.slice(0, labelW).padEnd(labelW, ' ');
  const valuePart = value.slice(-valueWidth).padStart(valueWidth, ' ');
  return `${labelPart} ${valuePart}`.slice(0, chars);
}

function centerLine(text: string, width: number): string {
  const value = String(text || '').trim();
  if (!value) return '';
  const line = value.length > width ? value.slice(0, width) : value;
  const leftPad = Math.max(0, Math.floor((width - line.length) / 2));
  return `${' '.repeat(leftPad)}${line}`;
}

/**
 * Sample receipt for printer settings test buttons.
 */
export async function printSampleThermalReceipt(
  charsPerLine: number,
  opts: { autoCut?: boolean; beep?: boolean },
  target: ThermalPrinterTarget,
): Promise<void> {
  const chars = Math.max(24, Math.min(48, charsPerLine || 32));
  const doc = new ThermalDocument({ width: 58, charsPerLine: chars });
  // Middle column must fit "Sub-Total" (9 chars); was 8 → truncated to "Sub-Tota".
  const priceW = 10;
  const amtW = 7;
  const itemW = Math.max(8, chars - priceW - amtW);
  const widths: [number, number, number] = [itemW, priceW, amtW];

  const ascii = (s: string) => s.replace(/[^\x20-\x7E]/g, ' ').trim();
  const testDate = ascii(new Date().toLocaleDateString());
  const testTime = ascii(new Date().toLocaleTimeString());

  doc
    .line('BizPlus POS', { align: 'center', bold: true, height: 2, width: 2 })
    .line('PRINTER TEST', { align: 'center', underline: true })
    .feed(1)
    .line(row(['# Item', 'Price', 'Amount'], widths), { bold: true })
    .line(row(['1.Coca Cola 500ml', '2x50', '100'], widths))
    .line(row(['2.Bread White 400g', '1x85', '85'], widths))
    .line(row(['3.Milk Fresh 1L', '1x120', '120'], widths))
    .feed(1)
    .line(row(['', 'Sub-Total', '305'], widths))
    .line(row(['', 'Discount', '0'], widths))
    .line(row(['', 'Total', 'Ksh305'], widths))
    .line('.'.repeat(28), { align: 'center' })
    .line(`Test Date: ${testDate}`)
    .line(`Test Time: ${testTime}`)
    .feed(1)
    .line('If you can read this clearly,', { align: 'center' })
    .line('your printer is working', { align: 'center' })
    .feed(1)
    .line('BizPlus POS - +254759266327', { align: 'center' });

  if (opts.beep) {
    doc.beep(1);
  }

  doc.cut('full', { feed: Platform.OS === 'web' ? 3 : 0 });

  await executePrint(doc, target);
}
